import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { dashboardAPI, seedAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Users, TrendUp, CurrencyDollar, Globe, Robot, Lightning,
  ArrowUp, ArrowDown
} from '@phosphor-icons/react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import { toast } from 'sonner';

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await dashboardAPI.overview();
      setData(res.data);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    try {
      await seedAPI.seed();
      toast.success('Demo data seeded! Refreshing...');
      loadData();
    } catch {
      toast.error('Failed to seed data');
    }
  };

  const metrics = data ? [
    { label: 'Total Clients', value: data.total_clients, icon: Users, color: 'var(--accent)', change: '+12%' },
    { label: 'Total Ad Spend', value: `$${data.total_spend.toLocaleString()}`, icon: CurrencyDollar, color: 'var(--success)', change: '+8.3%' },
    { label: 'Avg SEO Score', value: data.avg_seo_score, icon: TrendUp, color: 'var(--warning)', change: '+5.2' },
    { label: 'Avg ROAS', value: `${data.avg_roas}x`, icon: Lightning, color: 'var(--accent)', change: '+0.4x' },
    { label: 'Organic Traffic', value: data.total_traffic.toLocaleString(), icon: Globe, color: 'var(--success)', change: '+15%' },
    { label: 'Active Campaigns', value: data.active_campaigns, icon: Lightning, color: 'var(--warning)', change: '+3' },
    { label: 'Pages Managed', value: data.pages_managed, icon: Globe, color: 'var(--accent)', change: '+7' },
    { label: 'AI Tasks Done', value: data.ai_tasks_completed, icon: Robot, color: 'var(--success)', change: '+22' },
  ] : [];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[var(--accent-border)] border-t-[var(--accent)] rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-overview">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight">
              Dashboard
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              Overview of all client campaigns and performance
            </p>
          </div>
          {data?.total_clients === 0 && (
            <Button
              data-testid="seed-demo-data-btn"
              onClick={handleSeed}
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
            >
              <Lightning size={16} className="mr-2" />
              Seed Demo Data
            </Button>
          )}
        </div>

        {/* Metrics Grid */}
        {data?.total_clients === 0 ? (
          <div className="nx-metric-card rounded-lg text-center py-16">
            <Robot size={48} className="mx-auto mb-4 text-[var(--text-tertiary)]" />
            <h3 className="font-display text-lg font-bold text-[var(--text-primary)] mb-2">No Clients Yet</h3>
            <p className="text-[var(--text-secondary)] mb-4">Seed demo data or add your first client to get started</p>
            <Button
              data-testid="seed-demo-data-empty-btn"
              onClick={handleSeed}
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
            >
              Seed Demo Data
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="metrics-grid">
              {metrics.map((m, i) => {
                const Icon = m.icon;
                const isPositive = m.change.includes('+');
                return (
                  <div
                    key={m.label}
                    className="nx-metric-card rounded-lg animate-fade-in-up"
                    style={{ animationDelay: `${i * 50}ms` }}
                    data-testid={`metric-${m.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <Icon size={20} style={{ color: m.color }} weight="duotone" />
                      <span className={`text-xs font-mono flex items-center gap-0.5 ${isPositive ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                        {isPositive ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                        {m.change}
                      </span>
                    </div>
                    <p className="font-display text-xl sm:text-2xl font-bold text-[var(--text-primary)]">
                      {m.value}
                    </p>
                    <p className="text-xs text-[var(--text-tertiary)] mt-1 font-mono uppercase tracking-wider">
                      {m.label}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Traffic & Conversions Chart */}
              <div className="lg:col-span-2 nx-metric-card rounded-lg" data-testid="traffic-chart">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                  Traffic & Conversions (30d)
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.trends}>
                      <defs>
                        <linearGradient id="gTraffic" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="var(--accent)" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="gConversions" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--success)" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="var(--success)" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="date" tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip
                        contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 12 }}
                      />
                      <Area type="monotone" dataKey="traffic" stroke="var(--accent)" fill="url(#gTraffic)" strokeWidth={2} />
                      <Area type="monotone" dataKey="conversions" stroke="var(--success)" fill="url(#gConversions)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Ad Spend Chart */}
              <div className="nx-metric-card rounded-lg" data-testid="spend-chart">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                  Daily Spend (30d)
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.trends?.slice(-14)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="date" tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <Tooltip
                        contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 12 }}
                      />
                      <Bar dataKey="spend" fill="var(--accent)" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Top Clients */}
            {data.top_clients?.length > 0 && (
              <div className="nx-metric-card rounded-lg" data-testid="top-clients-table">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                  Top Clients by ROAS
                </h3>
                <table className="nx-table">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Industry</th>
                      <th>SEO Score</th>
                      <th>ROAS</th>
                      <th>Ad Spend</th>
                      <th>Traffic</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_clients.map((c) => (
                      <tr key={c.id}>
                        <td className="!text-[var(--text-primary)] font-medium">{c.display_name || c.name}</td>
                        <td><span className="nx-tag nx-tag-blue">{c.industry}</span></td>
                        <td>
                          <span className={`font-mono text-sm ${c.seo_score >= 70 ? 'text-[var(--success)]' : c.seo_score >= 50 ? 'text-[var(--warning)]' : 'text-[var(--error)]'}`}>
                            {c.seo_score}
                          </span>
                        </td>
                        <td className="font-mono text-sm text-[var(--success)]">{c.roas}x</td>
                        <td className="font-mono text-sm">${c.ad_spend?.toLocaleString()}</td>
                        <td className="font-mono text-sm">{c.organic_traffic?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
